import { inject, Injectable } from "@angular/core";
import { TiersApiService } from "./tiers-api.service";
import { TiersState, TiersStoreService } from "./tiers-store.service";
import { EntityFacadeService } from "../../../../shared/store/entity-store/entity-facade.service";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { Tiers } from "../../models/tiers.model";
import {
  Untrack,
  UpdateStatus,
} from "../../../../shared/store/utils/store-utils.service";
import { firstValueFrom } from "rxjs";
import { CertificatBio } from "../../models/certificat-bio.model";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";

@Injectable({ providedIn: "root" })
export class TiersFacadeService extends EntityFacadeService<
  Tiers,
  TiersApiService,
  TiersState,
  EntityMetadata,
  TiersStoreService
> {
  dialogs = inject(DialogsService);

  constructor() {
    const api = inject(TiersApiService);
    const store = inject(TiersStoreService);
    super(api, store);
    store.initStatus(TiersFacadeService);
  }

  initState() {
    this.store.patchState({
      stats: null,
    });
  }

  @Untrack
  @UpdateStatus
  async loadAllFilter(filters: { search?: string; page?: number }) {
    const page = filters.page ?? 0;
    if (page === 0) {
      this.store.removeAll();
    }
    const tiers = await firstValueFrom(this.api.loadAllFilter(filters));
    this.store.addMany(tiers);
    return tiers;
  }

  @Untrack
  @UpdateStatus
  async loadFournisseursForArticle(articleId: string) {
    const tiers = await firstValueFrom(
      this.api.loadFournisseursForArticle(articleId),
    );
    tiers.forEach((item) => {
      this.store.upsertOne(item);
    });
    return tiers;
  }

  @Untrack
  @UpdateStatus
  async getSuggestionForArticleId(articleId: string) {
    const tiers = await firstValueFrom(
      this.api.loadSuggestionForArticleId(articleId),
    );
    return tiers;
  }

  @Untrack
  @UpdateStatus
  async loadAllTransporteurs() {
    const tiers = await firstValueFrom(this.api.loadAllTransporteurs());
    tiers.forEach((item) => {
      this.store.upsertOne(item);
    });
    return tiers;
  }

  @Untrack
  @UpdateStatus
  async saveCertificat(certificat: CertificatBio) {
    const tiers = await firstValueFrom(this.api.saveCertificat(certificat));
    this.store.upsertOne(tiers);
    return tiers;
  }

  @Untrack
  @UpdateStatus
  async loadCertificat(tiersId: string) {
    return await firstValueFrom(this.api.loadCertificat(tiersId));
  }

  @Untrack
  @UpdateStatus
  async deleteCertificat(tiersId: string) {
    return await firstValueFrom(this.api.deleteCertificat(tiersId));
  }

  @UpdateStatus
  async stats(payload: {
    tiersId: string;
    dateDebut: Date;
    dateFin: Date;
    search: string;
  }) {
    const stats = await firstValueFrom(this.api.stats(payload));

    this.store.getWritableState().stats.set(stats);
  }
}
